import { Globe, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { secureOpenUrl } from '@/utils/linkValidation';

const TTProxyPricingSection = () => {
  // Convert USD to TTD (1 USD = 6.75 TTD)
  const proxyPlans = [
    {
      name: 'Natively Routed',
      pricePerIP: '$10.13 TTD',
      usdPrice: '$1.50 USD',
      brandName: 'WinRoute™',
      features: ['IPMask™ Protected', 'Technical Support', 'Basic Geo-tagging', '99.9% Uptime'],
      popular: false,
      colorScheme: 'teal',
      planUrl: 'https://acc.nexx.net/index.php?rp=/store/winroute-proxies'
    },
    {
      name: 'Native Pool',
      pricePerIP: '$13.50 TTD',
      usdPrice: '$2.00 USD', 
      brandName: 'VRed™',
      features: ['IPMask™ Protected', 'Premium Support', 'Advanced Geo-tagging', '99.9% Uptime'],
      popular: true,
      colorScheme: 'red',
      planUrl: 'https://acc.nexx.net/index.php?rp=/store/vred-proxies'
    },
    {
      name: 'Native Pool',
      pricePerIP: '$20.25 TTD',
      usdPrice: '$3.00 USD',
      brandName: 'ATNet™',
      features: ['IPMask™ Protected', 'Priority Support', 'Premium Geo-tagging', '99.9% Uptime'],
      popular: false,
      colorScheme: 'blue'
    }
  ];

  const getColorClasses = (colorScheme: string, popular: boolean) => {
    switch (colorScheme) {
      case 'teal':
        return {
          border: popular ? 'border-2 border-teal-500 shadow-xl' : 'border border-teal-200 shadow-lg',
          badge: 'bg-teal-500 text-white',
          brandBadge: 'bg-teal-600 text-white',
          price: 'text-teal-600',
          checkIcon: 'text-teal-500',
          button: 'bg-teal-500 hover:bg-teal-600 text-white'
        };
      case 'red':
        return {
          border: popular ? 'border-2 border-red-600 shadow-xl' : 'border border-red-200 shadow-lg',
          badge: 'bg-red-600 text-white', 
          brandBadge: 'bg-red-600 text-white',
          price: 'text-red-600',
          checkIcon: 'text-red-600',
          button: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'blue':
        return {
          border: popular ? 'border-2 border-blue-600 shadow-xl' : 'border border-blue-200 shadow-lg',
          badge: 'bg-blue-600 text-white',
          brandBadge: 'bg-blue-600 text-white', 
          price: 'text-blue-600',
          checkIcon: 'text-blue-600',
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
      default:
        return {
          border: 'border border-gray-200 shadow-lg',
          badge: 'bg-gray-600 text-white',
          brandBadge: 'bg-gray-600 text-white',
          price: 'text-gray-600', 
          checkIcon: 'text-gray-600',
          button: 'bg-gray-600 hover:bg-gray-700 text-white'
        };
    }
  };

  const handlePlanClick = (plan: any) => {
    if (plan.planUrl) {
      secureOpenUrl(plan.planUrl);
    }
  };

  return (
    <section className="py-12 bg-white/50 rounded-xl mb-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Globe className="h-8 w-8 text-red-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">
              US Residential Proxies - TTD Pricing
            </h2>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Premium residential proxy networks with transparent TTD pricing. Perfect for business automation and research.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {proxyPlans.map((plan, index) => {
            const colors = getColorClasses(plan.colorScheme, plan.popular);
            
            return (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 ${colors.border}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className={`${colors.badge} px-4 py-1 rounded-full text-sm font-medium`}>
                      Most Popular
                    </div>
                  </div>
                )}
                
                <div className="absolute top-4 right-4">
                  <div className={`${colors.brandBadge} px-3 py-1 rounded-lg text-xs font-bold`}>
                    {plan.brandName}
                  </div>
                </div>
                
                <div className="text-center mb-6 pt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-sm text-gray-500 mb-2">Per IP Address</div>
                  <div className={`text-3xl font-bold ${colors.price} mb-1`}>
                    🇹🇹 {plan.pricePerIP}
                  </div>
                  <div className="text-sm text-gray-400">
                    (was {plan.usdPrice})
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <Check className={`h-4 w-4 ${colors.checkIcon} mr-3 flex-shrink-0`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className={`w-full ${colors.button}`}
                  onClick={() => handlePlanClick(plan)}
                  disabled={!plan.planUrl}
                >
                  {plan.planUrl ? 'See Plans - Pay in TTD' : 'Contact for Pricing'}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-center mb-3">
            <Shield className="h-6 w-6 text-red-600 mr-2" />
            <h3 className="text-lg font-bold text-red-800">No Forex Hassle</h3>
          </div>
          <p className="text-red-700">
            All proxies come with our exclusive IPMask™ protection and can be paid for in Trinidad & Tobago Dollars. 
            Contact us to arrange TTD payment - no USD conversion required!
          </p>
        </div>
      </div>
    </section>
  );
};

export default TTProxyPricingSection;